import { useEffect, useMemo, useState } from "react";
import { GiPadlock, GiShield } from "react-icons/gi";
import { useBuild, useBuildingCatalog, useCastle, useSpeedup } from "../api/castle";
import { useApplySpeedup } from "../api/inventory";
import { useMe } from "../api/army";
import { Loading } from "./Loading";
import { ErrorView } from "./ErrorView";
import { ResearchScreen } from "./ResearchScreen";
import { BuildingRow } from "../components/BuildingRow";
import { BuildingDetailSheet } from "../components/BuildingDetailSheet";
import { HeartOfCastle } from "../components/HeartOfCastle";
import { CastleScene } from "../components/CastleScene";
import { ResourceBar } from "../components/ResourceBar";
import { ScreenBanner } from "../components/ScreenBanner";
import { useNow } from "../lib/ticker";
import { formatDuration, secondsUntil } from "../lib/format";
import { hapticImpact } from "../lib/telegram";
import { BuildOrder } from "../types/castle";
import { buildingDisplayName } from "../lib/buildingNames";

type CastleViewMode = "scene" | "list";
const VIEW_MODE_KEY = "castleViewMode";

function readInitialViewMode(): CastleViewMode {
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY);
    if (v === "scene" || v === "list") return v;
  } catch {
    // localStorage may be blocked inside Telegram WebView edge cases —
    // fall back to the default in that case.
  }
  return "scene";
}

export function CastleScreen() {
  const castle = useCastle();
  const catalog = useBuildingCatalog();
  const me = useMe();
  const build = useBuild();
  const speedup = useSpeedup();
  const applySpeedup = useApplySpeedup();
  const now = useNow(1000);
  const [openType, setOpenType] = useState<number | null>(null);
  // The central keep (Castle, type 0) opens the dedicated "Heart of Castle"
  // screen instead of the generic building sheet.
  const [heartOpen, setHeartOpen] = useState(false);
  // Toggled on when player taps "Open research" inside the Academy detail
  // sheet. While true the Castle screen swaps its body for ResearchScreen.
  const [showResearch, setShowResearch] = useState(false);
  // Pannable painterly scene vs the legacy stacked list. The list is kept
  // as a fallback for accessibility and for low-bandwidth situations where
  // the multi-MB background art is undesirable.
  const [viewMode, setViewMode] = useState<CastleViewMode>(readInitialViewMode);
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_KEY, viewMode);
    } catch {
      // ignore
    }
  }, [viewMode]);
  const playerElement = me.data?.element;

  const ordersByType = useMemo(() => {
    const m = new Map<number, BuildOrder>();
    castle.data?.state.activeOrders.forEach((o) => m.set(o.target, o));
    return m;
  }, [castle.data]);

  if (castle.isPending || catalog.isPending) {
    return <Loading message="Замок просыпается..." />;
  }
  if (castle.isError) {
    return <ErrorView message="Не получилось загрузить замок." />;
  }
  if (catalog.isError) {
    return <ErrorView message="Не получилось загрузить каталог зданий." />;
  }

  const data = castle.data!;
  const cat = catalog.data!;

  // Routes a tapped building: the central keep (type 0) opens the Heart of
  // Castle screen; everything else opens the generic detail sheet.
  const openBuilding = (t: number) => {
    hapticImpact("light");
    build.reset();
    speedup.reset();
    if (t === 0) {
      setHeartOpen(true);
    } else {
      setOpenType(t);
    }
  };

  const shieldHint = data.isBeginnerShielded
    ? (<><GiShield size={12} className="mr-1 inline" />Щит новичка</>)
    : data.isShielded && data.state.shieldUntil
    ? (<><GiShield size={12} className="mr-1 inline" />Щит {formatDuration(secondsUntil(data.state.shieldUntil, now))}</>)
    : null;

  if (showResearch) {
    return <ResearchScreen onBack={() => setShowResearch(false)} />;
  }

  return (
    <div className="flex h-full flex-col">
      <ScreenBanner kind="castle" title="Замок" subtitle={`Lvl ${data.castleLevel}`} />
      <ResourceBar resources={data.state.resources} safeStorage={data.safeStorage} />

      <div className="flex items-baseline justify-between px-4 py-2 text-sm">
        <div>
          <span className="text-tg-hint">Замок Lvl </span>
          <span className="font-medium">{data.castleLevel}</span>
          <span className="text-tg-hint"> · Сила </span>
          <span className="font-medium">
            {(data.powerBreakdown?.total ?? data.armyPower).toLocaleString("ru-RU")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {shieldHint ? <div className="text-tg-accent">{shieldHint}</div> : null}
          <button
            type="button"
            onClick={() => {
              hapticImpact("light");
              setViewMode(viewMode === "scene" ? "list" : "scene");
            }}
            className="rounded-full bg-tg-secondaryBg px-2.5 py-1 text-[11px] uppercase tracking-wide text-tg-hint active:bg-tg-bg"
          >
            {viewMode === "scene" ? "Список" : "Сцена"}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between px-4 pb-2 text-xs text-tg-hint">
        <div>
          Слоты постройки: {data.state.activeOrders.length}/{data.state.maxBuildSlots}
        </div>
        {build.isPending ? <div>Запускаю заказ…</div> : null}
        {build.error ? (
          <div className="text-tg-destructive">
            {(build.error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
              "Ошибка"}
          </div>
        ) : null}
      </div>

      {viewMode === "scene" ? (
        <div data-tutorial="buildings-list" className="flex-1 overflow-hidden">
          <CastleScene
            element={playerElement}
            castleLevel={data.castleLevel}
            buildings={data.state.buildings}
            catalogGet={(t) => cat.get(t)}
            onOpenBuilding={openBuilding}
          />
        </div>
      ) : (
      <div
        data-tutorial="buildings-list"
        className="flex-1 space-y-2 overflow-y-auto px-3 pb-3"
      >
        {(() => {
          // Buildings progressively reveal with Castle level (Lvl 1 → resource
          // basics, Lvl 3 → military, Lvl 5 → magic + march slots, Lvl 7 → heroes
          // + signature, Lvl 9 → endgame). Already-built buildings stay visible
          // even if they came in below today's reveal threshold. Locked ones get
          // grouped at the bottom as "coming soon" stubs grouped by reveal Lvl.
          const revealed: typeof data.state.buildings = [];
          const lockedByLevel = new Map<number, number[]>();
          for (const b of data.state.buildings) {
            const def = cat.get(b.type);
            const revealAt = def?.revealAtCastleLevel ?? 1;
            if (b.level > 0 || data.castleLevel >= revealAt) {
              revealed.push(b);
            } else {
              const arr = lockedByLevel.get(revealAt) ?? [];
              arr.push(b.type);
              lockedByLevel.set(revealAt, arr);
            }
          }
          const lockedGroups = Array.from(lockedByLevel.entries()).sort(
            ([a], [b]) => a - b,
          );

          return (
            <>
              {revealed.map((b) => {
                const def = cat.get(b.type);
                const displayName = buildingDisplayName(
                  b.type,
                  def?.name ?? `#${b.type}`,
                  playerElement,
                );
                return (
                  <BuildingRow
                    key={b.type}
                    state={b}
                    def={def}
                    displayName={displayName}
                    playerElement={playerElement}
                    activeOrder={ordersByType.get(b.type)}
                    now={now}
                    castleLevel={data.castleLevel}
                    maxAllowedWarHallLevel={data.maxAllowedWarHallLevel}
                    onOpen={() => openBuilding(b.type)}
                  />
                );
              })}

              {lockedGroups.map(([lvl, types]) => (
                <div
                  key={`locked-${lvl}`}
                  className="rounded-xl border border-dashed border-tg-hint/30 bg-tg-secondaryBg/40 p-3"
                >
                  <div className="text-xs uppercase tracking-wide text-tg-hint">
                    <span className="inline-flex items-center gap-1"><GiPadlock size={12} /> Откроется на Замке Lvl {lvl}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {types.map((t) => {
                      const name = buildingDisplayName(
                        t,
                        cat.get(t)?.name ?? `#${t}`,
                        playerElement,
                      );
                      return (
                        <span
                          key={t}
                          className="rounded-full bg-tg-bg/60 px-2 py-0.5 text-xs text-tg-hint"
                        >
                          {name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          );
        })()}
      </div>
      )}

      {heartOpen &&
        (() => {
          const keep = data.state.buildings.find((x) => x.type === 0);
          if (!keep) return null;
          const errorMsg =
            pickError(build.error) ?? pickError(speedup.error) ?? pickError(applySpeedup.error) ?? null;
          return (
            <HeartOfCastle
              element={playerElement}
              state={keep}
              def={cat.get(0)}
              activeOrder={ordersByType.get(0)}
              now={now}
              resources={data.state.resources}
              buildSpeedReductionPct={data.researchBonuses.buildSpeed}
              inventory={(data.state.inventory ?? {}) as Record<string, number>}
              activeBuildOrders={data.state.activeOrders.length}
              maxBuildSlots={data.state.maxBuildSlots}
              onClose={() => {
                setHeartOpen(false);
                build.reset();
                speedup.reset();
                applySpeedup.reset();
              }}
              onBuild={() => {
                build.mutate(0);
              }}
              onSpeedup={(seconds) => {
                speedup.mutate({ target: 0, seconds });
              }}
              onApplyInventorySpeedup={(itemId) => {
                applySpeedup.mutate({
                  itemId,
                  orderKind: 0, // SpeedupOrderKind.Build
                  buildingType: 0,
                });
              }}
              buildPending={build.isPending}
              speedupPending={speedup.isPending || applySpeedup.isPending}
              error={errorMsg}
            />
          );
        })()}

      {openType !== null &&
        (() => {
          const b = data.state.buildings.find((x) => x.type === openType);
          if (!b) return null;
          const def = cat.get(b.type);
          const displayName = buildingDisplayName(
            b.type,
            def?.name ?? `#${b.type}`,
            playerElement,
          );
          const errorMsg =
            pickError(build.error) ?? pickError(speedup.error) ?? pickError(applySpeedup.error) ?? null;
          return (
            <BuildingDetailSheet
              state={b}
              def={def}
              displayName={displayName}
              playerElement={playerElement}
              activeOrder={ordersByType.get(b.type)}
              now={now}
              castleLevel={data.castleLevel}
              resources={data.state.resources}
              buildSpeedReductionPct={data.researchBonuses.buildSpeed}
              inventory={(data.state.inventory ?? {}) as Record<string, number>}
              maxAllowedWarHallLevel={data.maxAllowedWarHallLevel}
              activeBuildOrders={data.state.activeOrders.length}
              maxBuildSlots={data.state.maxBuildSlots}
              onClose={() => {
                setOpenType(null);
                build.reset();
                speedup.reset();
                applySpeedup.reset();
              }}
              onOpenResearch={
                b.type === 12 // BuildingType.Academy
                  ? () => {
                      setOpenType(null);
                      setShowResearch(true);
                    }
                  : undefined
              }
              onBuild={() => {
                build.mutate(b.type, {
                  onSuccess: () => setOpenType(null),
                });
              }}
              onSpeedup={(seconds) => {
                speedup.mutate({ target: b.type, seconds });
              }}
              onApplyInventorySpeedup={(itemId) => {
                applySpeedup.mutate({
                  itemId,
                  orderKind: 0, // SpeedupOrderKind.Build
                  buildingType: b.type,
                });
              }}
              buildPending={build.isPending}
              speedupPending={speedup.isPending || applySpeedup.isPending}
              error={errorMsg}
            />
          );
        })()}
    </div>
  );
}

function pickError(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const resp = (err as { response?: { data?: { error?: string } } }).response;
  return resp?.data?.error ?? null;
}
